import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { LinkedAccountStrip } from '../components/LinkedAccountStrip';
import { BANK_DEFINITION_TYPES, ILinkedAccount, IPostLinkData, IServerErrorResponse } from '../../../types';
import { ReactComponent as IconAdd } from '../../../assets/icons/svg/shared/ic-shared-plus-circle.svg';
import PlaidActionButton from '../components/PlaidActionButton';
import { UserMessage } from '../../../DesignSystem/LiliUserMessage/UserMessage';
import UnlinkPlaidAccountConfirmation from '../components/Modals/UnlinkPlaidAccountConfirmation';
import { ReactComponent as CloseIcon } from '../../../assets/svg/shared/icon-close.svg';
import { useUnlinkAccountQuery } from '../../../hooks/query/useUnlinkAccountQuery';
import { useStore } from '../../../store';
import { trackMixPanel } from '../../../services/mixpanel.service';
import { getLinkedAccount } from '../utils';
import { UnlinkAccountToast } from '../components/Toasts/UnlinkAccountToast';
import AccountTypeModal, { ACCOUNT_TYPES } from '../components/AccountTypeModal';
import { useSetAccountTypeMutation } from '../../../hooks/mutations/useSetAccountTypeMutation';
import bugsnagHandler from '../../../services/bugsnag.service';
import { SendMoneyContext } from './SendMoney';

interface IPlaidLinkedAccountsProps {
  isLinkedAccountsLoading: boolean;
  linkedAccounts: ILinkedAccount[];
  preSelectedLinkedAccount: ILinkedAccount | null;
  handlePlaidMetaData: (data: IPostLinkData) => void;
  reFetchLinkedAccounts: () => void;
  onSubmit: (selectedLinkedAccount: ILinkedAccount | null) => void;
}

const STYLES = {
  WRAPPER: 'w-full h-full flex items-center py-[66px] flex-col bg-bw00',
  TITLE: 'font-metricRSemibold text-bw08 text-[25px] leading-9 text-center mb-[3px]',
  SUBTITLE: 'font-metricR text-bw07 text-[16px] leading-5 text-center',
  HEADER: '',
  ACCOUNTS_WRAPPER: 'my-[42px]',
  FOOTER: '',
  ADD_ANOTHER_ACCOUNT:
    'flex justify-center items-center h-[54px] w-[244px] bg-bw00 rounded border-[1px] border-action03 text-action04 hover:cursor-pointer',
};

const PlaidLinkedAccounts = (
  {
    isLinkedAccountsLoading,
    linkedAccounts,
    preSelectedLinkedAccount,
    handlePlaidMetaData,
    reFetchLinkedAccounts,
    onSubmit,
  }: IPlaidLinkedAccountsProps,
) => {
  const flowCtx = useContext(SendMoneyContext);
  const { t } = useTranslation();
  const { customerId } = useStore((state) => state.user);
  const {
    bankInfo: { bank },
  } = useStore((state) => state.getPrimaryAccount());
  const [selectedLinkedAccount, setSelectedLinkedAccount] = useState<ILinkedAccount | null>(preSelectedLinkedAccount);
  const [removeAccountExternalId, setRemoveAccountExternalId] = useState<string>('');
  const [showAccountTypeModal, setShowAccountTypeModal] = useState<boolean>(false);
  const { mutateAsync: setAccountType } = useSetAccountTypeMutation(() => (error: IServerErrorResponse) => {
    bugsnagHandler.notify(
      `Failed to set account type for customerId: ${customerId} with the error: ${error?.response?.data?.errors?.[0]?.message || ''}`,
    );
    onSubmit(selectedLinkedAccount);
  });
  const { refetch: unlinkAccount, isFetching: isUnlinkingAccountLoading } = useUnlinkAccountQuery(
    customerId,
    removeAccountExternalId,
  );
  const handleCloseUnlinkModal = () => setRemoveAccountExternalId('');
  const handleAccountClick = async (selectedAccount: ILinkedAccount) => {
    setSelectedLinkedAccount(selectedAccount);
    if (selectedAccount?.accountType) onSubmit(selectedAccount);
    else if (bank === BANK_DEFINITION_TYPES.SUNRISE || bank === BANK_DEFINITION_TYPES.CHOICE_TO_SUNRISE) {
      await setAccountType({
        customerId,
        linkedAccountId: selectedAccount?.externalId || '',
        accountType: ACCOUNT_TYPES.BUSINESS,
      });
      reFetchLinkedAccounts();
      onSubmit(selectedAccount);
    } else setShowAccountTypeModal(true);
  };
  const handleUnlinkAccount = () => {
    unlinkAccount()
      .then(() => {
        trackMixPanel('Unlink Plaid Account', {
          Origin: 'Send Money Screen',
          'Bank Name': getLinkedAccount(linkedAccounts, removeAccountExternalId)?.institutionName || '',
          'Unlink Method': 'Edit',
        });
        toast(<UnlinkAccountToast />, {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 2000,
          bodyClassName: 'p-0 ',
          className: 'w-[564px] h-[44px] bg-bw08 p-0 min-h-[44px] rounded-md top-[76px]',
          closeButton: false,
        });
        reFetchLinkedAccounts();
      })
      .finally(() => {
        setTimeout(() => {
          setRemoveAccountExternalId('');
        }, 0);
      });
  };

  const handleAccountTypeSuccess = () => {
    reFetchLinkedAccounts();
    onSubmit(selectedLinkedAccount);
  };

  useEffect(() => {
    if (linkedAccounts?.length === 0) {
      onSubmit(null);
    }
  }, [linkedAccounts]);

  useEffect(() => {
    flowCtx?.setChosenFee(null);
  }, []);

  const linkedAccountsList = linkedAccounts?.map((account: ILinkedAccount) => <LinkedAccountStrip key={`${account?.institutionId}_${account?.id}`} account={account} isSelected={selectedLinkedAccount?.externalId === account?.externalId && showAccountTypeModal} onClick={handleAccountClick} onRemoveClick={setRemoveAccountExternalId} />);
  return (
    <div className={STYLES.WRAPPER}>
      <div className={STYLES.HEADER}>
        <div className={STYLES.TITLE}>{t('SEND_MONEY.ACCOUNTS.TITLE')}</div>
        <div className={STYLES.SUBTITLE}>{t('SEND_MONEY.ACCOUNTS.SUBTITLE')}</div>
      </div>
      <div className={STYLES.ACCOUNTS_WRAPPER}>{linkedAccountsList}</div>
      <div className={STYLES.FOOTER}>
        <PlaidActionButton
          className={STYLES.ADD_ANOTHER_ACCOUNT}
          title={t('SEND_MONEY.ACCOUNTS.BTN_ADD_ANOTHER_ACCOUNT')}
          testId='plaidLinkedAccounts-addAnotherAccount'
          preIcon={<IconAdd className='mr-[5px] text-action03' />}
          callbackHandler={handlePlaidMetaData}
          isLoading={isLinkedAccountsLoading}
        />
        {removeAccountExternalId && (
          <UserMessage onClick={handleCloseUnlinkModal} id='SendMoney-accounts-unlink'>
            <CloseIcon
              className='absolute top-3 left-3 cursor-pointer z-10 rounded-full fill-bw07 hover:bg-bw02'
              onClick={handleCloseUnlinkModal}
            />
            <UnlinkPlaidAccountConfirmation
              onUnlinkAccount={handleUnlinkAccount}
              onClose={handleCloseUnlinkModal}
              isUnlinkingAccountLoading={isUnlinkingAccountLoading}
            />
          </UserMessage>
        )}
        {showAccountTypeModal && (
          <AccountTypeModal
            linkedAccountId={selectedLinkedAccount?.externalId || ''}
            onSuccess={handleAccountTypeSuccess}
            onClose={() => setShowAccountTypeModal(false)}
          />
        )}
      </div>
    </div>
  );
};

export default PlaidLinkedAccounts;
